<template>
  <DropdownTooltip
    :placement="placement"
    :skidding="skidding"
    class="emoji-dropdown"
    popperClass="emoji-dropdown__popper"
    iconSize="none"
  >
    <template #tooltip-label>
      <icon
        data="@icon/smile.svg"
        :fill="true"
        width="var(--icon-16)"
        height="var(--icon-16)"
        class="emoji-dropdown__icon-label"
      />
    </template>
    <template #tooltip-content="{ hide }">
      <div class="emoji-dropdown__content">
        <button
          v-for="(option, index) in options"
          :key="index"
          class="emoji-dropdown__item"
          @click="onSelect(option, hide)"
        >
          <img
            :src="option.src"
            :alt="option.type.toLowerCase()"
            class="emoji-dropdown__image"
          />
        </button>
      </div>
    </template>
  </DropdownTooltip>
</template>

<script lang="ts" setup>
import { computed } from "vue"

import DropdownTooltip from "@/components/Dropdown/DropdownTooltip.vue"

import { EmojiType } from "@/models"
import { EmojiData } from "@/models/Emoji"

interface EmojiOption {
  src: string
  type: EmojiType
  alt: string
}

const props = defineProps({
  placement: {
    type: String,
    default: "top-end"
  },
  hideAfterSelect: {
    type: Boolean,
    default: false
  },
  skidding: {
    type: Number,
    default: 16
  }
})

const emit = defineEmits<{
  (e: "onSelect", emoji: EmojiOption): void
}>()

function onSelect(emoji: EmojiOption, hide: () => void) {
  if (props.hideAfterSelect) {
    hide()
  }
  emit("onSelect", emoji)
}

const options = computed((): EmojiOption[] => {
  return Object.keys(EmojiData).map(type => {
    const emoji = EmojiData[type as EmojiType]
    return {
      src: emoji.src,
      type,
      alt: emoji.alt
    }
  })
})
</script>

<style scoped lang="scss">
@import "@/assets/scss/utils/mixins/_flex";

.emoji-dropdown {
  display: flex;
  width: fit-content;
  height: fit-content;

  &__content {
    max-width: 132px;

    @include flex($justify: space-between);

    flex-wrap: wrap;
    gap: var(--step2);
  }

  &__item {
    display: flex;
    padding: 2px;
    border-radius: var(--radius-item);
    cursor: pointer;

    &:hover {
      background: var(--monochrome-01);
    }
  }

  &__image {
    width: var(--icon-16);
    height: var(--icon-16);
  }
}

.dark {
  .emoji-dropdown {
    &__item {
      &:hover {
        background: var(--monochrome-16);
      }
    }
  }
}
</style>
